/**
 * SAHTE FİYAT ÖNBELLEĞİ + SAHTE EŞİTLEME MOTORU.
 *
 * Gerçekleri `@parafon/local` içinde (SQLite/Worker) ve orada test edildi;
 * burada yalnızca `?sahte=1` yolunun ve arayüz testlerinin ihtiyacı olan
 * GÖZLENEBİLİR DAVRANIŞ var:
 *   - eksik dönem "sıfır" değil EKSİK olarak döner,
 *   - istenemeyen (merkezde de olmayan) dönem `requestable: false` gelir,
 *   - aynı barı ikinci kez yazmak satır çoğaltmaz (fikirdeş).
 *
 * Hafta sonu/tatil sezgisi kasten BASİT: 3 iş gününden kısa delikler boşluk
 * sayılmaz (gerçeğinde `MIN_REAL_GAP_WEEKDAYS` aynı eşik). Bu sahte, kapsam
 * matematiğinin doğruluğunu KANITLAMAZ — onu `packages/local` testleri yapar.
 */

import type { Coverage, DailyBar, FxRate, InstrumentId, IsoDate } from "@parafon/contracts";
import type {
  CacheStats,
  CachedFxSeries,
  CloseLookup,
  DateRange,
  FxLookup,
  FxSyncResult,
  InstrumentCacheEntry,
  InstrumentCacheInput,
  MissingRange,
  PriceCachePort,
  PruneResult,
  PutBarsResult,
  PutFxResult,
  SyncCall,
  SyncEngine,
  SyncReport,
} from "@parafon/contracts/device-api";

const MIN_REAL_GAP_WEEKDAYS = 3;

const DAY_MS = 86_400_000;

/** Takvim günü farkı. Gerçeğinde `dates.ts` → `dayNumber`; burada aynı hesap. */
const dayNumber = (date: IsoDate): number =>
  Math.floor(new Date(`${date}T00:00:00Z`).getTime() / DAY_MS);

const isWeekend = (date: IsoDate): boolean => {
  const dow = new Date(`${date}T00:00:00Z`).getUTCDay();
  return dow === 0 || dow === 6;
};

const addDays = (date: IsoDate, days: number): IsoDate =>
  new Date(new Date(`${date}T00:00:00Z`).getTime() + days * DAY_MS).toISOString().slice(0, 10);

interface MutableEntry {
  instrumentId: InstrumentId;
  symbol: string | null;
  exchange: string | null;
  assetClass: string | null;
  currency: string | null;
  name: string | null;
  centralState: InstrumentCacheEntry["centralState"];
  backfilledTo: IsoDate | null;
  horizonFrom: IsoDate | null;
  horizonLimitedBy: InstrumentCacheEntry["horizonLimitedBy"];
  centralFrom: IsoDate | null;
  centralTo: IsoDate | null;
  pendingJobs: number | null;
  etaSeconds: number | null;
  lastError: string | null;
  lastSyncedAt: string | null;
  lastUsedAt: string;
  referenced: boolean;
}

export interface FakePriceCacheOptions {
  /** Başlangıç künyeleri — izleme ekranını veriyle açmak için. */
  readonly seed?: readonly InstrumentCacheInput[];
  /** Bu araçlar defterde geçiyor sayılır (budama koruması). */
  readonly referenced?: readonly InstrumentId[];
}

export interface FakePriceCache extends PriceCachePort {
  /** Testlerin bar sayısını doğrudan okuması için. */
  barCountOf(instrumentId: InstrumentId): number;
}

export function createFakePriceCache(options: FakePriceCacheOptions = {}): FakePriceCache {
  const entries = new Map<InstrumentId, MutableEntry>();
  const bars = new Map<InstrumentId, Map<IsoDate, DailyBar>>();
  /**
   * KUR SATIRI KAYNAĞIYLA BİRLİKTE TUTULUYOR. `FxRate` sözleşmesinde `source`
   * alanı YOK — kaynak yazarken `putFx(source, ...)` ile geliyor ve gerçeğinde
   * `fx_cache.source` kolonunda duruyor. `fxAsOf` onu geri vermek zorunda
   * (bkz. `price-cache-api.ts` → `FxLookup.source` gerekçesi: "bu kuru nereden
   * aldın" sorulabilir bir sorudur). Eskiden bu sahte `source`u ATIYORDU;
   * atılmış bir kaynağı `fxAsOf`ta uydurmak raporun denetlenebilirliğini
   * sessizce bozardı.
   */
  const fx = new Map<string, Map<IsoDate, { rate: FxRate; source: string }>>();
  const gaps = new Map<InstrumentId, DateRange[]>();
  const referenced = new Set(options.referenced ?? []);

  const nowStamp = (): string => new Date().toISOString();

  const entryOf = (instrumentId: InstrumentId): MutableEntry => {
    const found = entries.get(instrumentId);
    if (found !== undefined) return found;
    const fresh: MutableEntry = {
      instrumentId,
      symbol: null,
      exchange: null,
      assetClass: null,
      currency: null,
      name: null,
      centralState: "pending",
      backfilledTo: null,
      horizonFrom: null,
      horizonLimitedBy: null,
      centralFrom: null,
      centralTo: null,
      pendingJobs: null,
      etaSeconds: null,
      lastError: null,
      lastSyncedAt: null,
      lastUsedAt: nowStamp(),
      referenced: referenced.has(instrumentId),
    };
    entries.set(instrumentId, fresh);
    return fresh;
  };

  const barsOf = (instrumentId: InstrumentId): Map<IsoDate, DailyBar> => {
    const found = bars.get(instrumentId);
    if (found !== undefined) return found;
    const fresh = new Map<IsoDate, DailyBar>();
    bars.set(instrumentId, fresh);
    return fresh;
  };

  const envelope = (instrumentId: InstrumentId): { from: IsoDate | null; to: IsoDate | null } => {
    const dates = [...barsOf(instrumentId).keys()].sort();
    return { from: dates[0] ?? null, to: dates[dates.length - 1] ?? null };
  };

  const publish = (entry: MutableEntry): InstrumentCacheEntry => {
    const env = envelope(entry.instrumentId);
    return {
      ...entry,
      cachedFrom: env.from,
      cachedTo: env.to,
      barCount: barsOf(entry.instrumentId).size,
      referenced: referenced.has(entry.instrumentId) || entry.referenced,
    };
  };

  const applyInput = (input: InstrumentCacheInput): MutableEntry => {
    const entry = entryOf(input.instrumentId);
    if (input.symbol !== undefined) entry.symbol = input.symbol;
    if (input.exchange !== undefined) entry.exchange = input.exchange;
    if (input.assetClass !== undefined) entry.assetClass = input.assetClass;
    if (input.currency !== undefined) entry.currency = input.currency;
    if (input.name !== undefined) entry.name = input.name;
    if (input.centralState !== undefined && input.centralState !== null) {
      entry.centralState = input.centralState;
    }
    if (input.backfilledTo !== undefined) entry.backfilledTo = input.backfilledTo;
    if (input.horizonFrom !== undefined) entry.horizonFrom = input.horizonFrom;
    if (input.centralFrom !== undefined) entry.centralFrom = input.centralFrom;
    if (input.centralTo !== undefined) entry.centralTo = input.centralTo;
    if (input.pendingJobs !== undefined) entry.pendingJobs = input.pendingJobs;
    if (input.etaSeconds !== undefined) entry.etaSeconds = input.etaSeconds;
    if (input.lastError !== undefined) entry.lastError = input.lastError;
    if (input.lastSyncedAt !== undefined) entry.lastSyncedAt = input.lastSyncedAt;
    return entry;
  };

  for (const input of options.seed ?? []) applyInput(input);

  /**
   * Eksik dönem hesabı. Sıra önemli: merkezin kapsamı dışına düşen parça
   * `outside_central` + `requestable: false` olur; beklemekle DÜZELMEZ ve
   * arayüz onu "yükleniyor" diye göstermemeli.
   */
  const missing = (instrumentId: InstrumentId, from: IsoDate, to: IsoDate): MissingRange[] => {
    const entry = entries.get(instrumentId);
    const env = envelope(instrumentId);
    const declared = gaps.get(instrumentId) ?? [];

    const classify = (range: DateRange, reason: MissingRange["reason"]): MissingRange => {
      const centralFrom = entry?.centralFrom ?? null;
      const centralTo = entry?.centralTo ?? null;
      const outside =
        (centralFrom !== null && range.to < centralFrom) ||
        (centralTo !== null && range.from > centralTo);
      const known = declared.some((g) => g.from <= range.from && g.to >= range.to);
      if (known) return { ...range, reason: "known_gap", requestable: false };
      if (outside) return { ...range, reason: "outside_central", requestable: false };
      return { ...range, reason, requestable: true };
    };

    if (env.from === null || env.to === null) {
      return [classify({ from, to }, "no_cache")];
    }

    const out: MissingRange[] = [];
    if (from < env.from) out.push(classify({ from, to: addDays(env.from, -1) }, "before_cached"));
    if (to > env.to) out.push(classify({ from: addDays(env.to, 1), to }, "after_cached"));

    // İç delikler: yalnızca 3+ ARDIŞIK İŞ GÜNÜ eksikse boşluktur. Daha kısası
    // tatil olabilir ve doğrulanmış tatil takvimi YOK — uydurmuyoruz.
    const held = barsOf(instrumentId);
    let runStart: IsoDate | null = null;
    let runWeekdays = 0;
    const scanFrom = from > env.from ? from : env.from;
    const scanTo = to < env.to ? to : env.to;
    for (let d = scanFrom; d <= scanTo; d = addDays(d, 1)) {
      if (held.has(d)) {
        if (runStart !== null && runWeekdays >= MIN_REAL_GAP_WEEKDAYS) {
          out.push(classify({ from: runStart, to: addDays(d, -1) }, "interior_gap"));
        }
        runStart = null;
        runWeekdays = 0;
        continue;
      }
      if (runStart === null) runStart = d;
      if (!isWeekend(d)) runWeekdays += 1;
    }
    if (runStart !== null && runWeekdays >= MIN_REAL_GAP_WEEKDAYS) {
      out.push(classify({ from: runStart, to: scanTo }, "interior_gap"));
    }
    return out.sort((a, b) => a.from.localeCompare(b.from));
  };

  return {
    barCountOf(instrumentId) {
      return barsOf(instrumentId).size;
    },

    async putBars(instrumentId, _source, incoming, coverage): Promise<PutBarsResult> {
      const held = barsOf(instrumentId);
      const rejected: { date: IsoDate; reason: string }[] = [];
      let written = 0;
      for (const bar of incoming) {
        if (!/^\d{4}-\d{2}-\d{2}$/.test(bar.date)) {
          rejected.push({ date: bar.date, reason: "tarih biçimi geçersiz" });
          continue;
        }
        held.set(bar.date, bar); // fikirdeş: aynı gün çoğalmaz, güncellenir
        written += 1;
      }
      const entry = entryOf(instrumentId);
      entry.lastSyncedAt = nowStamp();
      if (coverage !== undefined && coverage !== null) {
        entry.centralFrom = coverage.from;
        entry.centralTo = coverage.to;
        entry.centralState = coverage.state;
      }
      const env = envelope(instrumentId);
      return {
        written,
        rejected,
        cachedFrom: env.from,
        cachedTo: env.to,
        barCount: held.size,
      };
    },

    /** P-d · Sahte önbellek anlık fiyat SAKLAMIYOR (sahte merkez de vermiyor). */
    async putSpot(quotes) {
      return {
        written: 0,
        rejected: quotes
          .filter((q) => q.quote !== null)
          .map((q) => ({ instrumentId: q.instrumentId, reason: "sahte önbellek anlık fiyat saklamıyor" })),
      };
    },

    async putCorporateActions() {
      return { written: 0, rejected: [] };
    },

    async corporateActions(instrumentId) {
      return { instrumentId, provided: null, coverage: null, actions: [] };
    },

    async putFx(source, rates): Promise<PutFxResult> {
      let written = 0;
      for (const rate of rates) {
        const key = `${rate.base}/${rate.quote}`;
        const series = fx.get(key) ?? new Map<IsoDate, { rate: FxRate; source: string }>();
        series.set(rate.date, { rate, source });
        fx.set(key, series);
        written += 1;
      }
      return { written, rejected: [] };
    },

    async readFx(base, quote, from, to): Promise<CachedFxSeries> {
      const series = fx.get(`${base}/${quote}`) ?? new Map<IsoDate, { rate: FxRate; source: string }>();
      const rates = [...series.values()]
        .map((row) => row.rate)
        .filter((r) => r.date >= from && r.date <= to)
        .sort((a, b) => a.date.localeCompare(b.date));
      const dates = rates.map((r) => r.date);
      const complete = dates.length > 0 && (dates[0] ?? "") <= from;
      return {
        base,
        quote,
        rates,
        from: dates[0] ?? null,
        to: dates[dates.length - 1] ?? null,
        missing: complete ? [] : [{ from, to, reason: "no_cache", requestable: true }],
        complete,
      };
    },

    /**
     * TEK GÜNÜN KAPANIŞI — SON FİYAT KURALI.
     *
     * Sorulan gün ya da öncesindeki EN SON bar. Bulunamazsa sayı DEĞİL eksik
     * döner; sahte olsun diye sıfır ya da uydurma bir fiyat üretilmiyor.
     *
     * EKSİKLİK BİLDİRİLİRKEN PENCERE UYDURULMUYOR (gerçeğiyle aynı kural):
     * alt sınır aracın ufku (`horizonFrom`), yoksa merkezin kapsam başlangıcı
     * (`centralFrom`), o da yoksa sorulan günün kendisi. "Şu tarihten beri hiç
     * fiyat yok" cümlesi böylece ölçülmüş bir tarihe dayanıyor.
     */
    async closeAsOf(instrumentId, date): Promise<CloseLookup> {
      const held = barsOf(instrumentId);
      const entry = entries.get(instrumentId);
      const asOf = [...held.keys()].filter((d) => d <= date).sort().pop();

      if (asOf === undefined) {
        const taban = entry?.horizonFrom ?? entry?.centralFrom ?? date;
        const from = taban > date ? date : taban;
        const eksik = missing(instrumentId, from, date);
        return {
          ok: false,
          instrumentId,
          date,
          missing:
            eksik.length > 0 ? eksik : [{ from, to: date, reason: "no_cache", requestable: true }],
        };
      }

      const bar = held.get(asOf) as DailyBar;
      return {
        ok: true,
        instrumentId,
        date,
        asOf,
        close: bar.close,
        adjClose: bar.adjClose ?? null,
        currency: entry?.currency ?? null,
        assetClass: entry?.assetClass ?? null,
        staleDays: dayNumber(date) - dayNumber(asOf),
        // Fiyat taze ise bayatlığın sebebi diye bir şey yok; aksi hâlde
        // ARADAKİ dönemin eksiklik sınıfı sorulur (istenebilir mi, yoksa
        // kaynakta da yok mu) — bekleyerek düzelip düzelmeyeceğini arayüz
        // ancak bununla söyleyebilir.
        gapsSince: asOf === date ? [] : missing(instrumentId, asOf, date),
      };
    },

    /**
     * TEK GÜNÜN KURU — son fiyat kuralıyla ve KAYNAĞIYLA.
     *
     * `base === quote` durumunda kaynak `"birim"`: 1'e 1 çevrim bir ölçüm
     * değil TANIM. Buraya "tcmb" yazmak hiç yapılmamış bir ölçümü kaynağa mal
     * etmek olurdu (gerçeğinde de aynı kural).
     */
    async fxAsOf(base, quote, date): Promise<FxLookup> {
      if (base === quote) {
        return { ok: true, base, quote, date, asOf: date, rate: "1", staleDays: 0, source: "birim" };
      }
      const series = fx.get(`${base}/${quote}`);
      const asOf =
        series === undefined ? undefined : [...series.keys()].filter((d) => d <= date).sort().pop();
      if (series === undefined || asOf === undefined) {
        return {
          ok: false,
          base,
          quote,
          date,
          reason: `${base}/${quote} için ${date} ve öncesinde hiç kur yok`,
        };
      }
      const row = series.get(asOf) as { rate: FxRate; source: string };
      return {
        ok: true,
        base,
        quote,
        date,
        asOf,
        rate: row.rate.rate,
        staleDays: dayNumber(date) - dayNumber(asOf),
        source: row.source,
      };
    },

    async missingRanges(instrumentId, from, to) {
      return missing(instrumentId, from, to);
    },

    async fetchPlan(instrumentId, from, to) {
      return missing(instrumentId, from, to)
        .filter((m) => m.requestable)
        .map((m) => ({ from: m.from, to: m.to }));
    },

    async putInstrument(input) {
      return publish(applyInput(input));
    },

    async instrumentState(instrumentId) {
      const entry = entries.get(instrumentId);
      return entry === undefined ? null : publish(entry);
    },

    async instrumentStates(instrumentIds) {
      const wanted = instrumentIds === undefined ? [...entries.keys()] : instrumentIds;
      return wanted
        .map((id) => entries.get(id))
        .filter((e): e is MutableEntry => e !== undefined)
        .map(publish);
    },

    async putGaps(instrumentId, ranges) {
      const list = gaps.get(instrumentId) ?? [];
      for (const range of ranges) {
        if (!list.some((g) => g.from === range.from && g.to === range.to)) list.push(range);
      }
      gaps.set(instrumentId, list);
      return ranges.length;
    },

    async touch(instrumentIds, now) {
      for (const id of instrumentIds) entryOf(id).lastUsedAt = now ?? nowStamp();
    },

    async cacheStats(): Promise<CacheStats> {
      const allDates = [...bars.values()].flatMap((m) => [...m.keys()]).sort();
      const barTotal = [...bars.values()].reduce((sum, m) => sum + m.size, 0);
      const byState: Record<string, number> = {};
      for (const entry of entries.values()) {
        byState[entry.centralState] = (byState[entry.centralState] ?? 0) + 1;
      }
      return {
        instruments: entries.size,
        bars: barTotal,
        fxRates: [...fx.values()].reduce((sum, m) => sum + m.size, 0),
        gaps: [...gaps.values()].reduce((sum, g) => sum + g.length, 0),
        oldestBar: allDates[0] ?? null,
        newestBar: allDates[allDates.length - 1] ?? null,
        byState,
        // Sahte depoda dosya yok; boyut TAHMİN. Gerçek sayıyı uydurmamak için
        // satır başına sabit bir kaba çarpan kullanılıyor ve arayüz bunun
        // sahte olduğunu ayrıca söylüyor.
        dbBytes: barTotal * 96,
        pageSize: 4096,
        tableBytes: null,
        quotaBytes: 500_000_000,
        usageBytes: barTotal * 96,
        quotaUsedRatio: barTotal === 0 ? 0 : (barTotal * 96) / 500_000_000,
      };
    },

    async prune(pruneOptions): Promise<PruneResult> {
      const allowReferenced = pruneOptions?.allowReferenced ?? false;
      const targets =
        pruneOptions?.instrumentIds === undefined
          ? [...entries.keys()]
          : [...pruneOptions.instrumentIds];
      const evicted: InstrumentId[] = [];
      const kept: InstrumentId[] = [];
      let deletedBars = 0;
      for (const id of targets) {
        const entry = entries.get(id);
        if (entry === undefined) continue;
        const isReferenced = referenced.has(id) || entry.referenced;
        if (isReferenced && !allowReferenced) {
          kept.push(id);
          continue;
        }
        deletedBars += barsOf(id).size;
        bars.delete(id);
        gaps.delete(id);
        entries.delete(id);
        evicted.push(id);
      }
      return {
        deletedBars,
        deletedFx: 0,
        deletedGaps: 0,
        evictedInstruments: evicted,
        keptReferenced: kept,
        barsRemaining: [...bars.values()].reduce((sum, m) => sum + m.size, 0),
      };
    },

    async clear(): Promise<PruneResult> {
      const deletedBars = [...bars.values()].reduce((sum, m) => sum + m.size, 0);
      const deletedFx = [...fx.values()].reduce((sum, m) => sum + m.size, 0);
      const evicted = [...entries.keys()];
      bars.clear();
      fx.clear();
      gaps.clear();
      entries.clear();
      return {
        deletedBars,
        deletedFx,
        deletedGaps: 0,
        evictedInstruments: evicted,
        keptReferenced: [],
        barsRemaining: 0,
      };
    },
  };
}

// ---------------------------------------------------------------------------
// Sahte eşitleme motoru
// ---------------------------------------------------------------------------

/**
 * Gerçeği `packages/local/src/browser/central/sync.ts`. Buradaki kopya
 * ALGORİTMANIN AYNISI DEĞİL, RAPORUN AYNISI: aynı alanları aynı anlamla
 * doldurur, böylece ekran gerçek raporla da doğru çalışır. Ayrışma riski
 * bilinçli ve sahte-yol ile sınırlı.
 */
export function createFakeSyncEngine(): SyncEngine {
  return {
    async syncPrices({ cache, central, instrumentIds, from, to }): Promise<SyncReport> {
      const t0 = Date.now();
      const ids = [...new Set(instrumentIds)];
      const upToDate: InstrumentId[] = [];
      const permanentlyMissing: SyncReport["permanentlyMissing"][number][] = [];
      const tracked: { instrumentId: InstrumentId; state: SyncReport["tracked"][number]["state"] }[] = [];
      const errors: string[] = [];
      const calls: SyncCall[] = [];
      let barsWritten = 0;
      let rejectedBars = 0;

      for (const id of ids) {
        try {
          const state = await cache.instrumentState(id);
          if (state !== null && state.centralState !== "pending") continue;
          const status = await central.track(id);
          await cache.putInstrument({
            instrumentId: id,
            centralState: status.state,
            backfilledTo: status.backfilledTo,
            horizonFrom: status.horizonFrom,
            horizonLimitedBy: status.horizonLimitedBy,
            pendingJobs: status.pendingJobs,
            etaSeconds: status.etaSeconds,
            ...(status.lastError === undefined ? {} : { lastError: status.lastError }),
          });
          tracked.push({ instrumentId: id, state: status.state });
        } catch (err) {
          errors.push(`track ${id}: ${messageOf(err)}`);
        }
      }

      const wanted = new Map<string, { range: DateRange; ids: InstrumentId[] }>();
      for (const id of ids) {
        try {
          const ranges = await cache.missingRanges(id, from, to);
          if (ranges.length === 0) {
            upToDate.push(id);
            continue;
          }
          const blocked = ranges.filter((r) => !r.requestable);
          if (blocked.length > 0) {
            permanentlyMissing.push({
              instrumentId: id,
              ranges: blocked.map((r) => ({ from: r.from, to: r.to })),
            });
          }
          for (const range of await cache.fetchPlan(id, from, to)) {
            const key = `${range.from}..${range.to}`;
            const group = wanted.get(key);
            if (group === undefined) wanted.set(key, { range, ids: [id] });
            else group.ids.push(id);
          }
        } catch (err) {
          errors.push(`missingRanges ${id}: ${messageOf(err)}`);
        }
      }

      for (const group of wanted.values()) {
        const c0 = Date.now();
        try {
          const response = await central.prices(group.ids, group.range.from, group.range.to);
          let written = 0;
          for (const series of response.series) {
            const coverage: Coverage = series.coverage;
            await cache.putInstrument({
              instrumentId: series.instrumentId,
              currency: series.currency === "" ? null : series.currency,
              centralState: coverage.state,
              centralFrom: coverage.from,
              centralTo: coverage.to,
            });
            if (coverage.gaps.length > 0) await cache.putGaps(series.instrumentId, coverage.gaps);
            const result = await cache.putBars(
              series.instrumentId,
              "central",
              series.bars,
              coverage,
            );
            written += result.written;
            rejectedBars += result.rejected.length;
          }
          barsWritten += written;
          calls.push({
            from: group.range.from,
            to: group.range.to,
            instrumentIds: group.ids,
            bars: written,
            ms: Date.now() - c0,
          });
        } catch (err) {
          const message = messageOf(err);
          errors.push(`prices ${group.range.from}..${group.range.to}: ${message}`);
          calls.push({
            from: group.range.from,
            to: group.range.to,
            instrumentIds: group.ids,
            bars: 0,
            ms: Date.now() - c0,
            error: message,
          });
        }
      }

      return {
        upToDate,
        permanentlyMissing,
        calls,
        tracked,
        barsWritten,
        rejectedBars,
        errors,
        totalMs: Date.now() - t0,
      };
    },

    async syncFx({ cache, central, pairs, from, to }): Promise<FxSyncResult> {
      const errors: string[] = [];
      const needed: (readonly [string, string])[] = [];
      let skippedPairs = 0;
      for (const [base, quote] of pairs) {
        try {
          const series = await cache.readFx(base, quote, from, to);
          if (series.complete) {
            skippedPairs += 1;
            continue;
          }
          needed.push([base, quote]);
        } catch (err) {
          errors.push(`readFx ${base}/${quote}: ${messageOf(err)}`);
        }
      }
      if (needed.length === 0) return { written: 0, skippedPairs, errors };
      try {
        const response = await central.fx(needed, from, to);
        const result = await cache.putFx("central", response.rates);
        return { written: result.written, skippedPairs, errors };
      } catch (err) {
        errors.push(`fx: ${messageOf(err)}`);
        return { written: 0, skippedPairs, errors };
      }
    },
  };
}

function messageOf(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}
